import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { LoadSuccessParams } from 'ag-grid-community';

import {
  COMPANY_POST_CHANGE_OWNER,
  COMPANY_POST_COPY_WORKSPACE,
  COMPANY_POST_CATEGORY,
  COMPANY_GET_ADMIN_ROLES,
  COMPANY_GET_CATEGORIES,
  COMPANY_GET_USER_CATEGORY_TREE,
  COMPANY_GET,
  COMPANY_GET_EMAIL_TEMPLATES,
  COMPANY_POST_PERMISSIONS,
  COMPANY_POST_DEFAULT_PERMISSIONS,
  COMPANY_GET_USER_ROLES,
  COMPANY_POST_UPDATE,
  COMPANY_PUT_CREATE,
  COMPANY_POST_MASS_OPERATION,
  COMPANY_POST_USER_TOGGLE,
  COMPANY_POST_CATEGORIES,
  COMPANY_POST_UPDATE_OBJECTS_PERMISSIONS,
  COMPANY_GET_FEATURES,
  COMPANY_GET_GET_AVAILABLE_CONNECTOR_TYPES,
  COMPANY_GET_STORIES,
} from '../endpoints/company.endpoints';

import {
  Company,
  CompanyCategory,
  CompanyFeature,
  DataObject,
  NomenclatureItem,
  ObjectDefinition,
  UserObjectsResponse,
} from 'src/app/core/models/core.models';
import {
  MassOperationType,
  NomConnectorObjectType,
} from 'src/app/core/constants';

import { toCamelCaseKeys, toQueryParams } from 'src/app/api/api-utils';
import {
  Permission,
  PermissionsResponse,
  Principal,
} from 'src/app/core/models/object.models';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface CopyWorkspaceParams {
  source_workspace_id: string[];
  target_company_id: string;
  target_user_id: string;
  copy_only_selected: boolean;
  feed_ids: string[];
  dashboard_ids: string[];
}

type ValidConnectorObjectType = Exclude<NomConnectorObjectType, null>;

export type ConnectorObjectTypeMap = {
  [key in ValidConnectorObjectType]?: boolean;
};

export interface CompanyStory {
  story_id: string;
  post_id: string;
  user_id: string;
  title: string;
  description: string;
  is_ai_generated: boolean;
  query_id: string;
  query_text: string;
  time_created: string;
  time_modified: string;
}

export interface ChangeOwnerPayload {
  principals: Principal[];
  applyToChildObjects: boolean;
  applyToSameOwnerChildObjectsOnly: boolean;
  objects: DataObject[];
}

export interface MassOperationResponse {
  data: UserObjectsResponse;
  message: string;
  operation: MassOperationType;
  status: 'success' | 'fail';
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class CompanyApiService {
  private readonly http = inject(HttpClient);

  getCompany(companyId: string): Observable<Company> {
    return this.http.get<Company>(COMPANY_GET(companyId));
  }

  /** Features of a company; without an id, the current company's. */
  getCompanyFeatures(companyId?: string): Observable<CompanyFeature[]> {
    return this.http.get<CompanyFeature[]>(COMPANY_GET_FEATURES(companyId));
  }

  getCompanyAvailableConnectorTypes(): Observable<ConnectorObjectTypeMap> {
    return this.http.get<ConnectorObjectTypeMap>(
      COMPANY_GET_GET_AVAILABLE_CONNECTOR_TYPES,
    );
  }

  getCompanyStories(): Observable<CompanyStory[]> {
    return this.http.get<CompanyStory[]>(COMPANY_GET_STORIES);
  }

  getAdminRoles(): Observable<NomenclatureItem[]> {
    return this.http.get<NomenclatureItem[]>(COMPANY_GET_ADMIN_ROLES);
  }

  getUserRoles(): Observable<NomenclatureItem[]> {
    return this.http.get<NomenclatureItem[]>(COMPANY_GET_USER_ROLES);
  }

  getCompanyEmailTemplates(): Observable<string[]> {
    return this.http.get<string[]>(COMPANY_GET_EMAIL_TEMPLATES);
  }

  getCategoriesAgGrid(request?: any): Observable<LoadSuccessParams> {
    // Hand-rolled, not toQueryParams: keys come dynamically from ag-grid's
    // filterModel, and an undefined filter is sent verbatim today.
    let httpParams = new HttpParams().set('output', 'agGrid');

    if (request?.filterModel) {
      Object.entries(request.filterModel).forEach(([key, value]: any) => {
        const filter = value?.filter;
        httpParams = httpParams.set(key, filter);
      });
    }

    return this.http.get<LoadSuccessParams>(COMPANY_GET_CATEGORIES, {
      params: httpParams,
    });
  }

  // TODO: Resolve duplication of following service in company and feed.service.ts
  getCategoriesTree = (
    companyCategoryIds: number[],
  ): Observable<CompanyCategory[]> => {
    const params = toQueryParams({
      'company_category_ids[]': companyCategoryIds,
    });

    return this.http.get<CompanyCategory[]>(COMPANY_GET_USER_CATEGORY_TREE, {
      params,
    });
  };

  copyCompanyWorkspace(params: CopyWorkspaceParams): Observable<any> {
    return this.http.post<any>(COMPANY_POST_COPY_WORKSPACE, { ...params });
  }

  updateCategoryTree(companyCategory: CompanyCategory): Observable<any> {
    // This endpoint's wire contract is camelCase (exemption to the snake_case rule)
    return this.http.post(
      COMPANY_POST_CATEGORIES,
      toCamelCaseKeys(companyCategory),
    );
  }

  createCategory(dataRow: any): Observable<CompanyCategory> {
    return this.http.post<CompanyCategory>(COMPANY_POST_CATEGORY, dataRow);
  }

  changeOwner(payload: ChangeOwnerPayload): Observable<UserObjectsResponse> {
    return this.http.post<UserObjectsResponse>(
      COMPANY_POST_CHANGE_OWNER,
      payload,
    );
  }

  createCompany(settingsInSnakeCase: any): Observable<Company> {
    return this.http.put<Company>(COMPANY_PUT_CREATE, settingsInSnakeCase);
  }

  updateCompany(settingsInSnakeCase: any): Observable<Company> {
    return this.http.post<Company>(COMPANY_POST_UPDATE, settingsInSnakeCase);
  }

  /**
   * Archive / unarchive / delete / undelete a set of objects.
   *
   * The endpoint answers 200 with `status: 'fail'` on an application-level
   * refusal, so the failure is raised here and the caller's rejection path handles
   * both that and transport errors.
   */
  massObjectOperation(
    operation: MassOperationType,
    objects: ObjectDefinition[],
  ): Observable<MassOperationResponse> {
    return this.http
      .post<MassOperationResponse>(COMPANY_POST_MASS_OPERATION, {
        action: operation,
        objects,
      })
      .pipe(
        map((response) => {
          if (response.status === 'fail') {
            throw new Error(response.message || 'Mass operation failed.');
          }
          return response;
        }),
        catchError((error) => {
          const message =
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred during mass operation.';
          return throwError(() => new Error(message));
        }),
      );
  }

  getObjectsPermissions(
    objects: ObjectDefinition[],
  ): Observable<PermissionsResponse> {
    return this.http.post<PermissionsResponse>(COMPANY_POST_PERMISSIONS, {
      objects,
      include_owners: true,
      simplify: true,
    });
  }

  getDefaultObjectClassPermissions(
    objectClassId: number,
  ): Observable<PermissionsResponse> {
    return this.http.post<PermissionsResponse>(
      COMPANY_POST_DEFAULT_PERMISSIONS,
      { object_class_id: objectClassId },
    );
  }

  updateObjectsPermissions(
    objects: DataObject[],
    permissions: Permission[],
  ): Observable<UserObjectsResponse> {
    return this.http.post<UserObjectsResponse>(
      COMPANY_POST_UPDATE_OBJECTS_PERMISSIONS,
      {
        objects,
        permissions,
      },
    );
  }

  toggleUserStatus(userId: string) {
    return this.http.post(COMPANY_POST_USER_TOGGLE, { userId });
  }
}
